import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetch from "auth/FetchInterceptor";

export const getAllModelCars = createAsyncThunk(
  "modelCar/getAllModelCars",
  async ({ page = 1, limit = 10, intitule = "" }, { rejectWithValue }) => {
    try {
      let url = `/modelCar/all?page=${page}&limit=${limit}`;
      if (intitule) {
        url += `&intitule=${encodeURIComponent(intitule)}`;
      }

      const response = await fetch({
        url,
        method: "get",
      });

      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement"
      );
    }
  }
);

export const addModelCar = createAsyncThunk(
  "modelCar/addModelCar",
  async (data, { rejectWithValue }) => {
    try {
      const response = await fetch({
        url: "/modelCar/create",
        method: "post",
        data,
      });
      return response;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400) {
        return rejectWithValue("Ce modèle existe déjà.");
      }

      return rejectWithValue("Erreur inconnue");
    }
  }
);

export const deleteModelCar = createAsyncThunk(
  "modelCar/deleteModelCar",
  async (id, { rejectWithValue }) => {
    try {
      await fetch({ url: `/modelCar/delete/${id}`, method: "delete" });
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur à la suppression"
      );
    }
  }
);

export const updateModelCar = createAsyncThunk(
  "modelCar/updateModelCar",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await fetch({
        url: `/modelCar/updateById/${id}`,
        method: "put",
        data,
      });
      // console.log(response);
      return { id, updated: response.data };
    } catch (error) {
      // console.log(error);
      const status = error?.response?.status;

      if (status === 400) {
        return rejectWithValue("Ce modèle existe déjà.");
      }

      return rejectWithValue("Erreur inconnue");
    }
  }
);

const modelCarSlice = createSlice({
  name: "modelCar",
  initialState: {
    modelCars: [],
    total: 0,
    page: 1,
    totalPages: 1,
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetModelCarState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // ----- getAllModelCars -----
    builder
      .addCase(getAllModelCars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllModelCars.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.modelCars = payload.data;
        state.total = payload.total;
        state.page = payload.page;
        state.totalPages = payload.totalPages;
      })
      .addCase(getAllModelCars.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // ----- addModelCar -----
    builder
      .addCase(addModelCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addModelCar.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;

        state.modelCars.unshift(payload);
        state.total += 1;
      })
      .addCase(addModelCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });

    // ----- deleteModelCar -----
    builder
      .addCase(deleteModelCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteModelCar.fulfilled, (state, { payload: deletedId }) => {
        state.loading = false;
        state.success = true;
        // On retire l’élément supprimé
        state.modelCars = state.modelCars.filter((m) => m.id !== deletedId);
        state.total -= 1;
      })
      .addCase(deleteModelCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });

    // ----- updateModelCar -----
    builder
      .addCase(updateModelCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateModelCar.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.success = true;
        // On met à jour l’élément dans le tableau
        const idx = state.modelCars.findIndex((m) => m.id === payload.id);
        if (idx !== -1) {
          state.modelCars[idx] = payload.updated;
        }
      })
      .addCase(updateModelCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });
  },
});

export const { resetModelCarState } = modelCarSlice.actions;
export default modelCarSlice.reducer;
